import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { X } from 'lucide-react';

import TwoFactorAuthSetup from './TwoFactorAuthSetup';
import FormField from '../../components/common/FormField';
import Modal from '../../components/common/Modal';
import { API_BASE_URL } from '../../config/config';
import {
  UserCreateDTO,
  UserEditDTO,
  AuthClient,
  UserClient,
  PermissionDTO,
} from '../../services/apiClient';
import axiosInstance from '../../services/axiosInstance';
import FileUpload from '../common/FileUpload';
import PasswordRequirements from '../common/PasswordRequirements';
import { useUserApi, useProfilePictureSasUrl } from '../../hooks/useUserApi';
import PermissionsTable from '../tables/PermissionsTable';
import { getPermissions, getUserId, getRefreshToken, setTokens } from '../../utils/tokenUtils';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface UserFormProps {
  isSubmitting: boolean;
  initialValues: UserCreateDTO | UserEditDTO;
  onSave: (user: UserCreateDTO | UserEditDTO) => void;
  onCancel?: () => void;
  setProfilePicture?: (file: File | null) => void;
  isCreating: boolean;
  showPermissions?: boolean;
}

// First, define the section type
type FormSection = {
  readonly id: 'profile-picture' | 'information' | 'credentials' | 'permissions';
  readonly title: string;
};

// Update the FORM_SECTIONS constant with the type
const FORM_SECTIONS: Record<string, FormSection> = {
  profilePicture: {
    id: 'profile-picture',
    title: 'Profile Picture',
  },
  information: {
    id: 'information',
    title: 'Information',
  },
  credentials: {
    id: 'credentials',
    title: 'Credentials',
  },
  permissions: {
    id: 'permissions',
    title: 'Permissions',
  },
} as const;

const UserForm: React.FC<UserFormProps> = ({
  initialValues,
  isSubmitting,
  onSave,
  onCancel,
  setProfilePicture,
  isCreating,
  showPermissions = true,
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [twoFactorSetupComplete, setTwoFactorSetupComplete] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState<
    PermissionDTO[]
  >([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [emailCheckTimeout, setEmailCheckTimeout] = useState<NodeJS.Timeout>();

  const permissions = getPermissions() || [];
  const currentUserId = getUserId();

  const canEditPasswords = permissions.includes('Settings.Passwords.Edit');
  const canEditPermissions = permissions.includes('Settings.Policies.Edit');
  const isEditingOwnProfile = !isCreating && currentUserId === (initialValues as UserEditDTO).id;
  const showPasswordFields = isCreating || canEditPasswords || isEditingOwnProfile;
  const showPermissionsSection = showPermissions && (isCreating || canEditPermissions);

  const authClient = new AuthClient(API_BASE_URL, axiosInstance);
  const userClient = new UserClient(API_BASE_URL, axiosInstance);

  const { getAllPermissions, checkEmailExists } = useUserApi();

  // Use TanStack Query hook for profile picture (automatically cached)
  const userId = !isCreating ? (initialValues as UserEditDTO).id : undefined;
  const { data: profilePictureUrl } = useProfilePictureSasUrl(userId);

  // Password validation states
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
  });

  useEffect(() => {
    if (
      !isCreating &&
      typeof (initialValues as UserEditDTO).twoFactorEnabled === 'boolean'
    ) {
      setTwoFactorEnabled(
        (initialValues as UserEditDTO).twoFactorEnabled ?? false,
      );
    }
  }, [isCreating, initialValues]);

  useEffect(() => {
    const fetchPermissions = async () => {
      const response = await getAllPermissions();
      if (response?.success && response.data) {
        setAvailablePermissions(response.data);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    return () => {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
    };
  }, [emailCheckTimeout]);

  const validatePassword = (password: string) => {
    setPasswordValidation({
      length: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
    });
  };

  const validateForm = (): boolean => {
    const formErrors: { [key: string]: string } = {};

    // Keep existing validations
    if (!formData.firstName?.trim()) {
      formErrors.firstName = 'First name is required.';
    }
    if (!formData.lastName?.trim()) {
      formErrors.lastName = 'Last name is required.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      formErrors.email = 'Valid email is required.';
    }

    // Check for existing email error
    if (errors.email) {
      formErrors.email = errors.email;
    }

    // Update password validation to check when editing if password fields are shown
    if (showPasswordFields && password.trim()) {
      if (!passwordValidation.length) {
        formErrors.password = 'Password must be at least 6 characters.';
      }
      if (!passwordValidation.uppercase) {
        formErrors.password = 'Password must contain at least one uppercase letter.';
      }
      if (!passwordValidation.lowercase) {
        formErrors.password = 'Password must contain at least one lowercase letter.';
      }
      if (!passwordValidation.digit) {
        formErrors.password = 'Password must contain at least one digit.';
      }

      if (password !== confirmPassword) {
        formErrors.confirmPassword = 'Passwords do not match.';
      }
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleInputChange =
    (key: keyof UserCreateDTO | keyof UserEditDTO) =>
      async (value: string): Promise<void> => {
        setFormData({
          ...formData,
          [key]: value,
        });

        // Check email exists only if it's the email field and the value has changed
        if (key === 'email' && value !== initialValues.email) {
          // Clear any existing timeout
          if (emailCheckTimeout) {
            clearTimeout(emailCheckTimeout);
          }

          // Set a new timeout to avoid too many API calls while typing
          const timeout = setTimeout(async () => {
            if (value.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              try {
                const response = await checkEmailExists(value);
                if (response.success && response.data) {
                  // Only set error if the email is different from the initial email
                  if (
                    value.toLowerCase() !== initialValues.email?.toLowerCase()
                  ) {
                    setErrors((prev) => ({
                      ...prev,
                      email: 'This email address is already in use.',
                    }));
                  } else {
                    // Clear email error if it's the user's own email
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.email;
                      return newErrors;
                    });
                  }
                } else {
                  // Clear email error if email doesn't exist
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.email;
                    return newErrors;
                  });
                }
              } catch (error) {
                console.error('Error checking email:', error);
              }
            }
          }, 500);

          setEmailCheckTimeout(timeout);
        }
      };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file && setProfilePicture) {
      setProfilePicture(file);
    }
  };

  const handlePasswordChange = (value: string): void => {
    setPassword(value);
    validatePassword(value);
  };

  const handleTwoFactorChange = async (enabled: boolean): Promise<void> => {
    setTwoFactorEnabled(enabled);
    if (enabled) {
      try {
        const response = await authClient.setupTwoFactor(
          (formData as UserEditDTO).id ?? '',
        );
        setQrCodeUrl(response.data?.qrCodeUrl ?? '');
        setShowTwoFactorModal(true);
      } catch (error) {
        console.error('Failed to setup two-factor authentication', error);
      }
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    const userPayload: UserCreateDTO | UserEditDTO = {
      ...formData,
      ...(showPasswordFields && password && { password }),
      twoFactorEnabled,
    };

    try {
      // Wait for the save operation to complete
      await onSave(userPayload);

      // If editing own profile, refresh the token AFTER save completes
      if (isEditingOwnProfile) {
        const refreshToken = getRefreshToken();

        if (refreshToken) {
          const authClient = new AuthClient(API_BASE_URL, axiosInstance);
          const response = await authClient.refreshToken({ refreshToken });

          if (response.success && response.data) {
            // Update tokens in appropriate storage (automatically handles impersonation)
            setTokens(
              response.data.accessToken ?? null,
              response.data.refreshToken ?? null
            );
          }
        }
      }
    } catch (error) {
      console.error('Error saving user or refreshing token:', error);
    }
  };

  const closeTwoFactorModal = (): void => {
    setShowTwoFactorModal(false);
    if (!twoFactorSetupComplete) {
      setTwoFactorEnabled(false); // Reset only if setup is not complete
    }
  };
  const handleTwoFactorVerification = async (
    code: string,
  ): Promise<boolean> => {
    try {
      const response = await authClient.verifyTwoFactor({
        userId: (formData as UserEditDTO).id,
        code,
      });

      if (response?.success) {
        setTwoFactorSetupComplete(true); // Mark setup as complete

        return true; // Verification successful
      }

      return false; // Verification failed
    } catch (error) {
      console.error('Failed to verify two-factor code', error);

      return false; // Treat error as failure
    }
  };

  const handlePermissionChange = (permission: string, isSelected: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: isSelected
        ? [...(prev.permissions || []), permission]
        : (prev.permissions || []).filter((p) => p !== permission),
    }));
  };

  // Update the sections logic right before the return statement
  const getVisibleSections = (): FormSection[] => {
    const visibleSections: FormSection[] = [
      FORM_SECTIONS.profilePicture,
      FORM_SECTIONS.information,
    ];

    if (showPasswordFields) {
      visibleSections.push(FORM_SECTIONS.credentials);
    }

    if (showPermissionsSection) {
      visibleSections.push(FORM_SECTIONS.permissions);
    }

    return visibleSections;
  };

  // Replace the existing sections declaration
  const sections = getVisibleSections();

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
    >
      <FormSection
        id={FORM_SECTIONS.profilePicture.id}
        title={FORM_SECTIONS.profilePicture.title}
      >
        {formData.profilePicture && profilePictureUrl && (
          <div className="relative flex items-center mt-4 mb-4">
            <div className="relative">
              <img
                src={profilePictureUrl}
                alt="Profile"
                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                className="rounded-full"
              />
              <button
                onClick={() => setFormData({ ...formData, profilePicture: '' })}
                className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                aria-label="Remove Profile Picture"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        <FileUpload
          id="profilePicture"
          title="Upload or drag and drop profile picture"
          accept={FileTypeConstants.IMAGES}
          onChange={handleFileChange}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.information.id}
        title={FORM_SECTIONS.information.title}
      >
        <FormField
          id="firstName"
          label="First Name"
          value={formData.firstName || ''}
          onChange={(e) => handleInputChange('firstName')(e.target.value)}
          placeholder="Enter first name"
          error={errors.firstName}
          textCase="titlecase"
        />
        <FormField
          id="lastName"
          label="Last Name"
          value={formData.lastName || ''}
          onChange={(e) => handleInputChange('lastName')(e.target.value)}
          placeholder="Enter last name"
          error={errors.lastName}
          textCase="titlecase"
        />
        <FormField
          id="email"
          label="Email"
          value={formData.email || ''}
          type='email'
          onChange={(e) => handleInputChange('email')(e.target.value)}
          placeholder="Enter email"
          textCase='lowercase'
          error={errors.email}
        />
        {!isCreating && (
          <FormField
            id="twoFactorEnabled"
            label="Enable Two-Factor Authentication?"
            type="checkbox"
            value={twoFactorEnabled}
            onChange={(e) =>
              handleTwoFactorChange((e.target as HTMLInputElement).checked)
            }
          />
        )}
      </FormSection>

      {showPasswordFields && (
        <FormSection
          id={FORM_SECTIONS.credentials.id}
          title={FORM_SECTIONS.credentials.title}
        >
          <div>
            <FormField
              id="password"
              label={isCreating ? "Password" : "New Password"}
              type="password"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              placeholder={isCreating ? "Enter password" : "Enter new password"}
              error={errors.password}
            />
            <PasswordRequirements validation={passwordValidation} />
          </div>
          <FormField
            id="confirmPassword"
            label={isCreating ? "Confirm Password" : "Confirm New Password"}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={isCreating ? "Confirm password" : "Confirm new password"}
            error={errors.confirmPassword}
          />
        </FormSection>
      )}

      {showPermissionsSection && (
        <FormSection
          id={FORM_SECTIONS.permissions.id}
          title={FORM_SECTIONS.permissions.title}
          columns={1}
        >
          <PermissionsTable
            permissions={availablePermissions}
            selectedPermissions={formData.permissions || []}
            onPermissionChange={handlePermissionChange}
          />
        </FormSection>
      )}

      {/* Modal for Two-Factor Authentication */}
      <Modal
        isOpen={showTwoFactorModal}
        onClose={closeTwoFactorModal}
        title="Two-Factor Authentication Setup"
      >
        <TwoFactorAuthSetup
          qrCodeUrl={qrCodeUrl ?? ''}
          onVerifyCode={handleTwoFactorVerification}
          onFinish={closeTwoFactorModal}
        />
      </Modal>
    </Form>
  );
};

export default UserForm;
